import type { EAnalyticMode } from '~/modules/analytics/types';
import type { GPaginationProps } from '~/presentation/common';
import type { IOrder } from '../types';
import type { ReactNode } from 'react';

import { useOrderListingFilterContext } from './OrderListingFilterProvider';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { ORDER_LISTING_ITEMS_PER_PAGE } from '../constants';
import { useGemXQlPagination } from '~/core/gemxql/hooks';
import { useOrderControlStore } from '../stores';
import { useFetchOrderListing } from '../hooks';

export interface IOrderListingDataContext {
  totalStoreOrders: number;
  orderListing: IOrder[];
  isLoading: boolean;
  isFetching: boolean;
  refetchOrderListing: () => void;
  paginationProps: GPaginationProps;
}

export const OrderListingDataContext = createContext<IOrderListingDataContext | undefined>(
  undefined,
);

interface IOrderListingDataProviderProps {
  mode: EAnalyticMode;
  children: ReactNode;
}

export const OrderListingDataProvider = ({ mode, children }: IOrderListingDataProviderProps) => {
  const isChangingFilter = useOrderControlStore((state) => state.isChangingFilter);
  const setIsChangingFilter = useOrderControlStore((state) => state.setIsChangingFilter);

  const { deferredQueryValue } = useOrderListingFilterContext();

  const { offset, buildPagination } = useGemXQlPagination({
    resetKey: isChangingFilter,
    itemsPerPage: ORDER_LISTING_ITEMS_PER_PAGE,
  });

  const {
    totalStoreOrders,
    totalFilteredOrders,
    orderListing,
    isLoading,
    isFetching,
    refetchOrderListing,
  } = useFetchOrderListing({
    mode,
    limit: ORDER_LISTING_ITEMS_PER_PAGE,
    offset,
    queryValue: deferredQueryValue,
  });

  const paginationProps = buildPagination({
    totalRecords: totalFilteredOrders,
    isLoading: isFetching,
  });

  useEffect(() => {
    if (isChangingFilter) {
      setIsChangingFilter(false);
    }
  }, [isChangingFilter, setIsChangingFilter]);

  const contextValue = useMemo(
    () => ({
      totalStoreOrders,
      orderListing,
      isLoading,
      isFetching,
      refetchOrderListing,
      paginationProps,
    }),
    [totalStoreOrders, orderListing, isLoading, isFetching, refetchOrderListing, paginationProps],
  );

  return (
    <OrderListingDataContext.Provider value={contextValue}>
      {children}
    </OrderListingDataContext.Provider>
  );
};

export const useOrderListingDataContext = () => {
  const context = useContext(OrderListingDataContext);
  if (!context) {
    throw new Error('GEMX_CONTEXT_IS_NOT_DEFINED');
  }
  return context;
};
